import { useState, useEffect } from "react";
import API from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Shield, ArrowCounterClockwise, CheckCircle, Warning } from "@phosphor-icons/react";

export default function BrandRefPolicyDrawer({ brand, open, onOpenChange, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [policyData, setPolicyData] = useState(null);

  const [hasOverride, setHasOverride] = useState(false);
  const [format, setFormat] = useState("url");
  const [fallback, setFallback] = useState("sub_id1");
  const [passthrough, setPassthrough] = useState(false);
  const [refUrl, setRefUrl] = useState("");
  const [status, setStatus] = useState("active");

  useEffect(() => {
    if (!brand || !open) return;
    const fetchPolicy = async () => {
      setLoading(true);
      try {
        const { data } = await API.get(`/admin/brands/${brand.campaign_id}/ref-policy`);
        setPolicyData(data);
        const bPol = data.brand_policy;
        const eff = data.effective_policy || {};

        if (bPol && (bPol.status === "active" || bPol.status === true)) {
          setHasOverride(true);
          setFormat(bPol.format || eff.format || "url");
          setFallback(bPol.fallback || eff.fallback || "sub_id1");
          setPassthrough(!!bPol.passthrough);
          setRefUrl(bPol.ref_url || "");
          setStatus(bPol.status || "active");
        } else {
          setHasOverride(false);
          setFormat(eff.format || "url");
          setFallback(eff.fallback || "sub_id1");
          setPassthrough(!!eff.passthrough);
          setRefUrl(eff.ref_url || "");
          setStatus("active");
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load referrer policy");
      } finally {
        setLoading(false);
      }
    };
    fetchPolicy();
  }, [brand, open]);

  const handleSave = async () => {
    if (!brand) return;
    setSaving(true);
    try {
      if (!hasOverride) {
        // Clear override -> revert to network
        await API.delete(`/admin/brands/${brand.campaign_id}/ref-policy`);
        toast.success(`Reverted ${brand.name || brand.campaign_id} to Network Policy`);
      } else {
        const payload = {
          format,
          fallback,
          passthrough,
          ref_url: refUrl,
          status,
        };
        await API.put(`/admin/brands/${brand.campaign_id}/ref-policy`, payload);
        toast.success(`Updated Referrer Policy for ${brand.name || brand.campaign_id}`);
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save referrer policy");
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = async () => {
    if (!brand) return;
    setSaving(true);
    try {
      await API.delete(`/admin/brands/${brand.campaign_id}/ref-policy`);
      toast.success(`Removed custom policy override for ${brand.name || brand.campaign_id}`);
      setHasOverride(false);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to revert policy");
    } finally {
      setSaving(false);
    }
  };

  const netPolicy = policyData?.inherited_network_policy || {};

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center gap-2">
            <Shield size={20} className="text-emerald-600" />
            <SheetTitle>Brand Referrer Policy</SheetTitle>
          </div>
          <SheetDescription>
            Configure custom traffic masking and referrer URL shaping for{" "}
            <span className="font-semibold text-slate-900">{brand?.name}</span> (ID: {brand?.campaign_id}).
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="py-12 text-center text-sm text-slate-400">Loading referrer policy...</div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Status Inheritance Banner */}
            <div className={`p-3.5 rounded-lg border text-xs space-y-1 ${
              hasOverride
                ? "bg-amber-50/70 border-amber-200 text-amber-900"
                : "bg-emerald-50/70 border-emerald-200 text-emerald-900"
            }`}>
              <div className="flex items-center gap-2 font-semibold text-sm">
                {hasOverride ? (
                  <>
                    <Warning size={16} className="text-amber-600" />
                    <span>Custom Brand Policy Override Active</span>
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} className="text-emerald-600" />
                    <span>Inheriting Network Policy ({policyData?.network})</span>
                  </>
                )}
              </div>
              <p className="text-slate-600">
                {hasOverride
                  ? "This brand uses custom referrer rules that override the network defaults."
                  : `Using default referrer policy configured for network '${policyData?.network}'.`}
              </p>
            </div>

            {/* Toggle Enable Brand Override */}
            <div className="flex items-center justify-between p-3 border border-slate-200 rounded-md bg-slate-50">
              <div>
                <label className="text-sm font-medium text-slate-900 block">Enable Brand Override</label>
                <span className="text-xs text-slate-500">
                  Override network defaults specifically for this brand
                </span>
              </div>
              <Switch checked={hasOverride} onCheckedChange={setHasOverride} />
            </div>

            {/* Policy Inputs */}
            <div className={`space-y-4 transition-opacity ${!hasOverride ? "opacity-60 pointer-events-none" : ""}`}>
              {/* Format */}
              <div>
                <label className="text-xs font-semibold uppercase text-slate-500 block mb-1">
                  Referrer Format
                </label>
                <Select value={format} onValueChange={setFormat}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select format" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="url">Full Article URL (e.g. https://site.com/page)</SelectItem>
                    <SelectItem value="host">Bare Hostname (e.g. site.com)</SelectItem>
                    <SelectItem value="none">Send Nothing (Masked/Empty)</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-[11px] text-slate-400 mt-1 block">
                  Network default: <span className="font-mono text-slate-700">{netPolicy.format || "url"}</span>
                </span>
              </div>

              {/* Fallback */}
              <div>
                <label className="text-xs font-semibold uppercase text-slate-500 block mb-1">
                  Fallback Value (When no referrer URL present)
                </label>
                <Select value={fallback} onValueChange={setFallback}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select fallback" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sub_id1">Use Publisher Sub ID (sub_id1)</SelectItem>
                    <SelectItem value="homepage">WordPress Site Homepage</SelectItem>
                    <SelectItem value="fixed">Fixed Target URL (ref_url below)</SelectItem>
                    <SelectItem value="none">Empty / None</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-[11px] text-slate-400 mt-1 block">
                  Network default: <span className="font-mono text-slate-700">{netPolicy.fallback || "sub_id1"}</span>
                </span>
              </div>

              {/* Fixed Ref URL */}
              {(fallback === "fixed" || refUrl) && (
                <div>
                  <label className="text-xs font-semibold uppercase text-slate-500 block mb-1">
                    Fixed Referrer URL
                  </label>
                  <Input
                    type="url"
                    placeholder="https://wildbloomfamilies.com/"
                    value={refUrl}
                    onChange={(e) => setRefUrl(e.target.value)}
                    className="text-sm font-mono"
                  />
                </div>
              )}

              {/* Passthrough */}
              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <div>
                  <label className="text-sm font-medium text-slate-900 block">Bypass Traffic Masking (Passthrough)</label>
                  <span className="text-xs text-slate-500">
                    Allow browser Referer header to reach network directly
                  </span>
                </div>
                <Switch checked={passthrough} onCheckedChange={setPassthrough} />
              </div>
            </div>
          </div>
        )}

        <SheetFooter className="flex items-center justify-between sm:justify-between gap-2 pt-4 border-t">
          {hasOverride ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRevert}
              disabled={saving || loading}
              className="text-slate-600 hover:text-slate-900"
            >
              <ArrowCounterClockwise size={14} className="mr-1" /> Revert to Network
            </Button>
          ) : (
            <div />
          )}

          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={handleSave} disabled={saving || loading}>
              {saving ? "Saving..." : "Save Policy"}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
